import {PDFDocument} from 'pdf-lib'

const getUrlExtension = (url: string) => {
  const path = url.split('?')[0].toLowerCase()
  return path.split('.').pop() || ''
}

const isPdfLabel = (url: string, contentType: string) =>
  getUrlExtension(url) === 'pdf' || contentType.includes('pdf')

const isPngLabel = (url: string, contentType: string) =>
  getUrlExtension(url) === 'png' || contentType.includes('png')

const isJpgLabel = (url: string, contentType: string) => {
  const ext = getUrlExtension(url)
  return ext === 'jpg' || ext === 'jpeg' || contentType.includes('jpeg')
}

export const fetchShippingLabelBytes = async (url: string) => {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Label fetch failed with status ${response.status}`)
  }

  return {
    bytes: await response.arrayBuffer(),
    contentType: response.headers.get('content-type') || '',
  }
}

export const mergeShippingLabelUrls = async (urls: string[]) => {
  const mergedPdf = await PDFDocument.create()
  const failedUrls: string[] = []
  let mergedPageCount = 0

  for (const url of urls) {
    try {
      const {bytes, contentType} = await fetchShippingLabelBytes(url)

      if (isPdfLabel(url, contentType)) {
        const pdfDoc = await PDFDocument.load(bytes)
        const pages = await mergedPdf.copyPages(pdfDoc, pdfDoc.getPageIndices())
        for (const page of pages) {
          mergedPdf.addPage(page)
          mergedPageCount += 1
        }
        continue
      }

      if (isPngLabel(url, contentType)) {
        const image = await mergedPdf.embedPng(bytes)
        const page = mergedPdf.addPage([image.width, image.height])
        page.drawImage(image, {x: 0, y: 0, width: image.width, height: image.height})
        mergedPageCount += 1
        continue
      }

      if (isJpgLabel(url, contentType)) {
        const image = await mergedPdf.embedJpg(bytes)
        const page = mergedPdf.addPage([image.width, image.height])
        page.drawImage(image, {x: 0, y: 0, width: image.width, height: image.height})
        mergedPageCount += 1
        continue
      }

      throw new Error(`Unsupported label format for ${url}`)
    } catch (error) {
      console.error(`Failed to merge label from ${url}:`, error)
      failedUrls.push(url)
    }
  }

  if (!mergedPageCount) {
    throw new Error('No pages merged')
  }

  return {
    pdfBytes: await mergedPdf.save(),
    failedUrls,
    mergedPageCount,
  }
}

const getDownloadExtension = (url: string, contentType: string) => {
  const ext = getUrlExtension(url)
  if (ext) return ext
  if (contentType.includes('png')) return 'png'
  if (contentType.includes('jpeg')) return 'jpg'
  if (contentType.includes('pdf')) return 'pdf'
  return 'bin'
}

export const downloadShippingLabelUrl = async (url: string, filename: string) => {
  try {
    const {bytes, contentType} = await fetchShippingLabelBytes(url)
    const extension = getDownloadExtension(url, contentType)
    const hasExtension = filename.includes('.')
    const blob = new Blob([bytes], {type: contentType || 'application/octet-stream'})
    const objectUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = objectUrl
    link.download = hasExtension ? filename : `${filename}.${extension}`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
    return true
  } catch (error) {
    console.error(`Failed to download label from ${url}:`, error)
    return false
  }
}

export const downloadShippingLabelsIndividually = async (urls: string[]) => {
  let successCount = 0
  let failedCount = 0

  for (let index = 0; index < urls.length; index += 1) {
    const downloaded = await downloadShippingLabelUrl(urls[index], `shipping-label-${index + 1}`)
    if (downloaded) {
      successCount += 1
    } else {
      failedCount += 1
    }

    if (index < urls.length - 1) {
      await new Promise((resolve) => window.setTimeout(resolve, 300))
    }
  }

  return {successCount, failedCount}
}
